'use client'

import * as Headless from '@headlessui/react'
import NextLink from 'next/link'
import type React from 'react'
import { forwardRef } from 'react'
import { cn } from '../../shared/cn'

export type LinkProps = React.ComponentPropsWithoutRef<'a'> & {
  href: string
  /**
   * Client-side prefetch of the target route. Defaults to `false`: pages are
   * `force-dynamic` on a single VPS (no CDN), so prefetching every in-viewport
   * link would hammer the server. Opt in per-link where snappy nav matters.
   */
  prefetch?: boolean
  /**
   * Runs on a plain client-side navigation (not on a modified click that opens a new tab); calling
   * `event.preventDefault()` lets the caller take over, e.g. with a shallow URL update.
   */
  onNavigate?: React.ComponentProps<typeof NextLink>['onNavigate']
}

/**
 * App-internal navigation link. Wraps `next/link` so same-origin hrefs do a
 * client-side (soft) navigation instead of a full page reload. External,
 * `mailto:`, `tel:` and hash hrefs fall back to normal anchor behaviour.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { className, href, prefetch = false, ...props },
  ref
) {
  return (
    <Headless.DataInteractive
      as={NextLink}
      {...props}
      href={href}
      prefetch={prefetch}
      ref={ref}
      className={cn('cursor-pointer text-contentPrimary', className)}
    />
  )
})
